import type {
  AudienceRule,
  ActionSource,
  ABTEventsSchema,
  ABTAttributeData,
  ActionSourceSurvey,
  ActionSourceChatbot,
  ActionSourceSurveyV2,
  ActionSourceVoicebot,
  ABTPossibleRelations,
  ActionTypeAudienceRule,
  ActionSourceExternalLinkV2,
} from 'src/types/audience-actions';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {
  TranslationFunction,
  MakeKeyFunction,
} from 'src/types/language-support';

import map from 'lodash/map';
import moment from 'moment';
import toLower from 'lodash/toLower';
import includes from 'lodash/includes';
import uniqueId from 'lodash/uniqueId';
import {moduleTypeLabelMap} from 'src/utils/actions-survey-questions';
import {truncateString} from '@spaced-out/ui-design-system/lib/utils/string';
import {
  INCLUDE_ALL_VALUE,
  ABT_DSL_CREATOR_HELPER,
} from 'src/utils/audience-revamp';


type relationOptions = Array<
  MenuOption & {
    value: Array<string>;
  }
>;

export const getFilteredEvents = (
  query: string,
  events: ABTEventsSchema = [],
): ABTEventsSchema => {
  const queryLowerCase = toLower(query);
  const filteredEvents = events.map((entityEvent) => {
    const filteredAttributes = entityEvent.attributes.filter(
      ({label, description, attribute_name}) =>
        includes(toLower(label), queryLowerCase) ||
        includes(toLower(description), queryLowerCase) ||
        includes(toLower(attribute_name), queryLowerCase),
    );

    return {
      ...entityEvent,
      attributes: filteredAttributes,
    };
  });

  return filteredEvents.filter(({attributes}) => attributes.length > 0);
};

export const getABTRelationOptionsMap = (
  relations: ABTPossibleRelations,
): Map<string, relationOptions> => {
  const relationsOptionMap: Map<string, relationOptions> = new Map();

  for (const entityType of Object.keys(relations)) {
    const options: relationOptions = [];
    const relationsConfigArray = relations[entityType];

    for (const schemaConfigs of relationsConfigArray) {
      const relationConfig = schemaConfigs;
      const names = map(relationConfig, 'name').filter(Boolean);
      const displayNames = map(relationConfig, 'display_name').filter(Boolean);

      options.push({
        key: names.join('/'),
        label: displayNames.join('/'),
        value: names,
      });
    }

    relationsOptionMap.set(entityType, options);
  }

  return relationsOptionMap;
};

export const getABTAttributesMap = (
  events: ABTEventsSchema = [],
): Map<string, ABTAttributeData> => {
  const ABTAttributesMap = new Map();

  for (const abtEvent of events) {
    const {
      label: entityLabel,
      entity_type: entityType,
      description: entityDescription,
      attributes,
    } = abtEvent;
    for (const abtEventAttribute of attributes) {
      const {
        label: attributeLabel,
        description: attributeDescription,
        attribute_name: attributeName,
      } = abtEventAttribute;
      ABTAttributesMap.set(attributeName, {
        entityType,
        entityLabel,
        attributeName,
        attributeLabel,
        entityDescription,
        attributeDescription,
      });
    }
  }

  return ABTAttributesMap;
};

export const getABTAttributeLabel = (
  attributeName: string,
  ABTAttributesMap: Map<string, ABTAttributeData>,
  t: TranslationFunction,
  makeKey: MakeKeyFunction,
): string => {
  if (ABTAttributesMap.has(attributeName)) {
    const {entityLabel = '', attributeLabel = attributeName} =
      ABTAttributesMap.get(attributeName) ?? {};
    return `${t(makeKey(entityLabel), entityLabel)} - ${t(
      makeKey(attributeLabel),
      attributeLabel,
    )}`;
  }
  return attributeName;
};

export const getEntityVariableLabel = (
  attributeName: string,
  fields: Array<{
    label: string;
    resolvedId: string;
  }>,
): string => {
  for (const entityEvent of fields) {
    const {label, resolvedId} = entityEvent;
    if (resolvedId === attributeName) {
      return label;
    }
  }
  return attributeName;
};

export const getABTRule = (
  entityType: string,
  attributeName: string,
  baseEntityType: string,
  ABTRelationOptionMap: Map<string, relationOptions>,
): ActionTypeAudienceRule => {
  const fromDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
  const defaultRelation = ABTRelationOptionMap.get(entityType)?.[0].value ?? [];

  return {
    id: uniqueId('rule'),
    ruleType: 'abt_rule_new',
    abtEntityType: entityType,
    baseEntityType,
    abtAttributeName: attributeName,
    abtRelationArray: defaultRelation,

    // TODO: create a config for this similar to old ABT (ABT_ATTRIBUTE_KEY_SET)
    toDate: 'now',
    fromDate,
    scopeValue: 'specific_date_range',
    journeyValue: null,
    questionValue: null,
    journeyOperator: 'eq',
    touchpointValue: null,
    touchpointOperator: null,
  };
};

export const getRecipientEntityBySelectedRelation = (
  relationKey: string,
  relations?: Array<
    Array<{
      entity_type: string;
      display_name: string;
      name: string;
    }>
  >,
): string => {
  if (!Array.isArray(relations) || relations.length === 0) {
    return '';
  }

  for (const schemaConfigs of relations) {
    const namesKey = map(schemaConfigs, 'name').filter(Boolean).join('/');
    if (relationKey === namesKey) {
      return schemaConfigs[schemaConfigs.length - 2].entity_type || '';
    }
  }

  return '';
};

export const getRecipientEntityType = (
  baseEntityType: string,
  abtRelationArray: Array<string>,
  possibleRelations: Array<
    Array<{
      entity_type: string;
      display_name: string;
      name: string;
    }>
  >,
): string => {
  if (abtRelationArray.length === 1) {
    return baseEntityType;
  } else if (abtRelationArray.length > 1) {
    const relationKey = abtRelationArray.filter(Boolean).join('/');
    return getRecipientEntityBySelectedRelation(relationKey, possibleRelations);
  }
  return '';
};

export const getJourneyName = (
  journeyID: string,
  sourceDetails: Array<ActionSource>,
  isSurvey: boolean,
): string => {
  if (journeyID === 'all') {
    return 'All Journeys';
  }

  for (const journey of sourceDetails) {
    const {id, name, touchpoints = []} = journey;
    if (id === journeyID) {
      return `${truncateString(name, 25)} (${touchpoints.length} ${
        isSurvey ? 'surveys' : 'touchpoints'
      })`;
    }
  }
  return truncateString(journeyID, 25);
};

export const getTooltipText = (
  journeyID: string,
  sourceDetails: Array<ActionSource>,
  isSurvey: boolean,
): {
  tooltipBody: string;
  tooltipTitle: string;
} => {
  const journey = sourceDetails.find(({id}) => id === journeyID);
  const touchpointNames =
    journey?.touchpoints?.map(({name}) => name).join(', \n') || '';

  return {
    tooltipBody: touchpointNames,
    tooltipTitle: isSurvey ? 'Surveys Selected' : 'Touchpoints Selected',
  };
};

export const getWorkflowTooltipText = (
  workflowId: string,
  sourceDetailsJ2: Array<ActionSource>,
): {
  tooltipBody: string;
  tooltipTitle: string;
} => {
  const workflow = sourceDetailsJ2.find(({id}) => id === workflowId);
  const nodeNames = workflow?.nodes?.map(({name}) => name).join(', \n') || '';

  return {
    tooltipBody: nodeNames,
    tooltipTitle: 'Nodes Selected',
  };
};

export const getWorkflowName = (
  workflowId: string,
  sourceDetails: Array<ActionSource>,
): string => {
  if (workflowId === 'all') {
    return 'All Workflows';
  }

  for (const workflow of sourceDetails) {
    const {id, name, nodes = []} = workflow;
    if (id === workflowId) {
      return `${truncateString(name, 25)} (${nodes.length} nodes)`;
    }
  }
  return truncateString(workflowId, 25);
};

export const getChatbotName = (
  chatbotId: string,
  sourceDetails: Array<ActionSourceChatbot>,
): string => {
  for (const chatbot of sourceDetails) {
    const {flowId, name} = chatbot;
    if (flowId === chatbotId) {
      return `${truncateString(name, 25)}`;
    }
  }
  return truncateString(chatbotId, 25);
};

export const getValidABTEventsList = (
  ABTEventsList: ABTEventsSchema,
  ABTRelationsOptionsMap: Map<string, relationOptions>,
): ABTEventsSchema => {
  return ABTEventsList.filter(({entity_type}) => {
    const relationOptions = ABTRelationsOptionsMap.get(entity_type);
    const isValid = !!relationOptions?.length;

    // console.log("hello : -- ",entity_type , ' is ', isValid);
    // Return the validity condition
    return isValid;
  });
};

export const getSurveyQuestionObj = (
  journeys: Array<ActionSource>,
  surveyId: string,
): ActionSourceSurvey => {
  const journeyObj = journeys.length ? journeys[0] : null;
  const touchpoint = journeyObj?.touchpoints?.length
    ? journeyObj.touchpoints[0]
    : null;
  const surveys = touchpoint?.surveys || [];

  const filteredSurveys = surveys.filter(({id}) => id === surveyId);
  return filteredSurveys[0] || null;
};

export const filterTouchpoints = (
  journeys: Array<ActionSource>,
  touchpointValue: Array<string> | string,
): Array<ActionSource> => {
  return journeys.map((journey) => {
    const {touchpoints: currentTouchpoints} = journey;
    return {
      ...journey,
      touchpoints:
        currentTouchpoints?.filter(({id}) =>
          Array.isArray(touchpointValue)
            ? touchpointValue.includes(id)
            : id === touchpointValue,
        ) || [],
    };
  });
};

export const filterNodes = (
  workflows: Array<ActionSource>,
  nodeValue: Array<string> | string,
): Array<ActionSource> => {
  return workflows.map((workflow) => {
    const {nodes: currentNodes} = workflow;
    return {
      ...workflow,
      nodes:
        currentNodes?.filter(({id}) =>
          Array.isArray(nodeValue) ? nodeValue.includes(id) : nodeValue === id,
        ) || [],
    };
  });
};

export const filterTouchpointsExternalLinks = (
  journeys: Array<ActionSource>,
  touchpointValue: Array<string> | string,
  externalLinkValue: Array<string> | string,
): Array<ActionSource> => {
  return journeys.map((journey) => {
    const {touchpoints: currentTouchpoints = []} = journey;

    return {
      ...journey,
      touchpoints:
        currentTouchpoints
          .filter(({id}) =>
            Array.isArray(touchpointValue)
              ? touchpointValue.includes(id)
              : id === touchpointValue,
          )
          .map((touchpoint) => ({
            ...touchpoint,
            external_links: touchpoint.external_links
              ? touchpoint.external_links.filter((link) =>
                  Array.isArray(externalLinkValue)
                    ? externalLinkValue.includes(link)
                    : link === externalLinkValue,
                )
              : [],
          })) || [],
    };
  });
};

export const getAbtDisplayValues = (
  rule: AudienceRule,
  extraArgs: {
    recipientEnity?: string;
    surveyObj?: ActionSourceSurvey;
    journeyName?: string;
    touchpointName?: string;
    isSourceLoading?: boolean;
    sourceValue?: string;
    sourceJ1Value?: string;
    sourceJ2Value?: string;
    chatbotSourceValue?: string;
    voicebotSourceValue?: string;
    surveyV2SourceValue?: string;
    externalLinkV2SourceValue?: string;
    externalLinkArray?: Array<string> | string | null | undefined;
  },
  t: (arg1: string, arg2: string) => string,
): string[] => {
  const result: Array<string> = [];
  if (!rule.abtEntityType) {
    return result;
  }

  const addTimeline = () => {
    if (rule.fromDate && rule.toDate) {
      result.push(
        `${t('Timeline', 'Timeline')} - ${t('from', 'from')} ${
          rule.fromDate ?? ''
        } ${t('to', 'to')} ${rule.toDate ?? ''}`,
      );
    }
  };

  switch (rule.abtEntityType) {
    case 'action_email':
      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (extraArgs.sourceValue) {
        result.push(
          `${t('Email_is_one_of', 'Email is one of')} - ${
            extraArgs.sourceValue ?? ''
          }`,
        );
      } else if (rule.journeyValue === INCLUDE_ALL_VALUE) {
        result.push('All Journeys');
      }
      if (extraArgs.isSourceLoading) {
        result.push('loading...', 'loading...');
      }
      break;

    case 'action_email_v2':
      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (extraArgs.sourceValue) {
        result.push(
          `${t('Email_is_one_of', 'Email is one of')} - ${
            extraArgs.sourceValue ?? ''
          }`,
        );
      } else if (rule.workflowValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Workflows', 'All Workflows'));
      }
      if (extraArgs.isSourceLoading) {
        result.push('loading...', 'loading...');
      }
      break;

    case 'action_survey':
      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (extraArgs.sourceValue) {
        result.push(
          `${t('Survey_is_one_of', 'Survey is one of')} - ${
            extraArgs.sourceValue ?? ''
          }`,
        );
      } else if (rule.journeyValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Surveys', 'All Surveys'));
      }
      if (extraArgs.isSourceLoading) {
        result.push('loading...', 'loading...');
      }
      break;

    case 'action_survey_question':
      const {question, module_type} = extraArgs.surveyObj || {};
      const journeyName =
        extraArgs.journeyName || t('Unknown_Journey', 'Unknown Journey');
      const touchpointName =
        extraArgs.touchpointName ||
        t('Unknown_Touchpoint', 'Unknown Touchpoint');

      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (question && module_type) {
        const moduleLabel =
          (moduleTypeLabelMap(t)[module_type] &&
            moduleTypeLabelMap(t)[module_type].label) ||
          t('Unknown_Module', 'Unknown Module');
        result.push(
          `${t('Question', 'Question')} - (${moduleLabel}) ${question}`,
        );
      }
      result.push(`${t('From', 'From')} - ${journeyName} - ${touchpointName}`);
      if (extraArgs.surveyObj) {
        const responseValue = Array.isArray(rule.responseValue)
          ? rule.responseValue.join(', ')
          : rule.responseValue || t('No_Response', 'No Response');
        rule.responseValue || t('No_Response', 'No Response');
        const operatorLabel = getResponseOperatorLabel(
          // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
          module_type,
          rule.responseOperator || '',
          t,
        );
        result.push(
          `${t('Response', 'Response')} ${operatorLabel} ${responseValue}`,
        );
      }
      if (extraArgs.isSourceLoading) {
        result.push('loading...', 'loading...', 'loading...');
      }
      break;
    case 'action_external_link':
      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (extraArgs.journeyName && extraArgs.touchpointName) {
        result.push(
          `${t('From', 'From')} - ${extraArgs.journeyName ?? ''} - ${
            extraArgs.touchpointName ?? ''
          }`,
        );
      } else if (extraArgs.isSourceLoading) {
        result.push(`${t('loading', 'loading')}...`);
      }

      if (rule.externalLinkValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_External_Links', 'All External Links'));
      } else if (rule.externalLinkValue) {
        const externalLinkValue = Array.isArray(rule.externalLinkValue)
          ? rule.externalLinkValue.join(', ')
          : rule.externalLinkValue || t('No_Link', 'No Link');
        result.push(
          `${t(
            'External_link_is_one_of',
            'External link is one of',
          )} ${externalLinkValue}`,
        );
      }
      break;
    case 'chatbot_action':
      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (extraArgs.chatbotSourceValue) {
        result.push(
          `${t('Chatbot_is_one_of', 'Chatbot is one of')} - ${
            extraArgs.chatbotSourceValue ?? ''
          }`,
        );
      } else if (rule.chatbotValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Chatbots', 'All Chatbots'));
      }

      if (extraArgs.sourceJ1Value) {
        result.push(
          `${t('Source_is_one_of', 'Source is one of')} - ${
            extraArgs.sourceJ1Value ?? ''
          }`,
        );
      } else if (rule.journeyValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Journeys', 'All Journeys'));
      }
      if (extraArgs.sourceJ2Value) {
        result.push(
          `${t('Source_is_one_of', 'Source is one of')} - ${
            extraArgs.sourceJ2Value ?? ''
          }`,
        );
      } else if (rule.workflowValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Workflows', 'All Workflows'));
      }
      if (extraArgs.isSourceLoading) {
        result.push('loading...', 'loading...');
      }
      break;

    case 'action_sms':
      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (extraArgs.sourceValue) {
        result.push(
          `${t('SMS_is_one_of', 'SMS is one of')} - ${
            extraArgs.sourceValue ?? ''
          }`,
        );
      } else if (rule.workflowValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Workflows', 'All Workflows'));
      }
      if (extraArgs.isSourceLoading) {
        result.push('loading...', 'loading...');
      }
      break;

    case 'action_whatsapp':
      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (extraArgs.sourceValue) {
        result.push(
          `${t('Whatsapp_is_one_of', 'Whatsapp is one of')} - ${
            extraArgs.sourceValue ?? ''
          }`,
        );
      } else if (rule.workflowValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Workflows', 'All Workflows'));
      }
      if (extraArgs.isSourceLoading) {
        result.push('loading...', 'loading...');
      }
      break;

    case 'action_voice_ai':
      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (extraArgs.voicebotSourceValue) {
        result.push(
          `${t('Voicebot_is_one_of', 'Voicebot is one of')} - ${
            extraArgs.voicebotSourceValue ?? ''
          }`,
        );
      } else if (rule.voicebotValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Voicebots', 'All Voicebots'));
      }

      if (extraArgs.sourceJ2Value) {
        result.push(
          `${t('Source_is_one_of', 'Source is one of')} - ${
            extraArgs.sourceJ2Value ?? ''
          }`,
        );
      } else if (rule.workflowValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Workflows', 'All Workflows'));
      }
      if (extraArgs.isSourceLoading) {
        result.push('loading...', 'loading...');
      }
      break;

    case 'action_survey_v2':
      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (extraArgs.surveyV2SourceValue) {
        result.push(
          `${t('Survey_is_one_of', 'Survey is one of')} - ${
            extraArgs.surveyV2SourceValue ?? ''
          }`,
        );
      } else if (rule.surveyV2Value === INCLUDE_ALL_VALUE) {
        result.push(t('All_Surveys', 'All Surveys'));
      }

      if (extraArgs.sourceJ2Value) {
        result.push(
          `${t('Source_is_one_of', 'Source is one of')} - ${
            extraArgs.sourceJ2Value ?? ''
          }`,
        );
      } else if (rule.workflowValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Workflows', 'All Workflows'));
      }
      if (extraArgs.isSourceLoading) {
        result.push('loading...', 'loading...');
      }
      break;

    case 'action_external_link_v2':
      if (extraArgs.recipientEnity) {
        result.push(extraArgs.recipientEnity);
      } else {
        result.push(`${t('loading', 'loading')}...`);
      }
      addTimeline();
      if (extraArgs.externalLinkV2SourceValue) {
        result.push(
          `${t('External_Link_is_one_of', 'External Link is one of')} - ${
            extraArgs.externalLinkV2SourceValue ?? ''
          }`,
        );
      } else if (rule.externalLinkV2Value === INCLUDE_ALL_VALUE) {
        result.push(t('All_External_Links', 'All External Links'));
      }

      if (extraArgs.sourceJ2Value) {
        result.push(
          `${t('Source_is_one_of', 'Source is one of')} - ${
            extraArgs.sourceJ2Value ?? ''
          }`,
        );
      } else if (rule.workflowValue === INCLUDE_ALL_VALUE) {
        result.push(t('All_Workflows', 'All Workflows'));
      }
      if (extraArgs.isSourceLoading) {
        result.push('loading...', 'loading...');
      }
      break;

    default:
      return [];
  }

  return result;
};

export const getResponseOperatorLabel = (
  module_type: string,
  responseOperator: string,
  t: (arg1: string, arg2: string) => string,
): string => {
  // Check if module_type exists in the map
  const moduleConfig = moduleTypeLabelMap(t)[module_type];

  if (!moduleConfig) {
    return 'Unknown Operator';
  }
  // Find the matching operator in the operatorOptions array
  const operator = moduleConfig.operatorOptions.find(
    (option) => option.value === responseOperator,
  );
  return operator ? operator.label : 'Unknown Operator';
};

export const pluralize = (
  count: number,
  word: string,
  suffix: string = 's',
): string => {
  return count === 1 ? word : `${word}${suffix}`;
};

export const formatJourneySummary = (
  sourceDetails: Array<ActionSource>,
  term: string = 'survey',
): string => {
  /**
   * Generate a comma-separated string with the format:
   * <Journey Name> - (<count of touchpoints> survey/surveys, touchpoint/touchpoints)
   *
   * @param {Array} sourceDetails - Array containing journey details.
   * @param {string} term - Term to display ('survey' or 'touchpoint'). Default is 'survey'.
   * @returns {string} Formatted string.
   */

  const results = sourceDetails.map((journey) => {
    const journeyName = journey.name || 'Unknown Journey';
    const touchpoints = journey.touchpoints || [];
    const count = touchpoints.length; // Always count all touchpoints
    const displayTerm = pluralize(count, term); // Pluralize term based on count

    return `${journeyName} (${count} ${displayTerm})`;
  });

  return results.join(', \n');
};

export const formatWorkflowSummary = (
  sourceDetails: Array<ActionSource>,
  term: string = 'node',
  suffix: string = 's',
): string => {
  /**
   * Generate a comma-separated string with the format:
   * <Workflow Name> - (<count of nodes> node/nodes)
   *
   * @param {Array} sourceDetails - Array containing workflow details.
   * @param {string} term - Term to display ('node' or 'sms'). Default is 'node'.
   * @param {string} suffix - Suffix to add to the term ('s' or 'es'). Default is 's'.
   * @returns {string} Formatted string.
   */

  const results = sourceDetails.map((workflow) => {
    const workflowName = workflow.name || 'Unknown Workflow';
    const nodes = workflow.nodes || [];
    const count = nodes.length; // Always count all touchpoints
    const displayTerm = pluralize(count, term, suffix);

    return `${workflowName} (${count} ${displayTerm})`;
  });

  return results.join(', \n').trim();
};

export const formatChatbotSummary = (
  chatbots: Array<ActionSourceChatbot>,
): string => {
  const chatbotNames = chatbots.map((chatbot) => {
    return chatbot.name;
  });
  return chatbotNames.join(', \n').trim();
};

export const formatVoicebotSummary = (
  voicebots: Array<ActionSourceVoicebot>,
): string => {
  const voicebotNames = voicebots.map((voicebot) => {
    return voicebot.name;
  });
  return voicebotNames.join(', \n').trim();
};

export const formatSurveyV2Summary = (
  surveys: Array<ActionSourceSurveyV2>,
): string => {
  const surveyNames = surveys.map((survey) => {
    return survey.name;
  });
  return surveyNames.join(', \n').trim();
};

export const formatExternalLinkV2Summary = (
  externalLinks: Array<ActionSourceExternalLinkV2>,
): string => {
  const externalLinkNames = externalLinks.map((externalLink) => {
    return externalLink.name;
  });
  return externalLinkNames.join(', \n').trim();
};

export const filterUnSupportedEvents = (
  actionEvents: ABTEventsSchema,
): ABTEventsSchema => {
  const supportedEvents = Object.keys(ABT_DSL_CREATOR_HELPER);
  return actionEvents.filter((actionEvent) =>
    supportedEvents.includes(actionEvent.entity_type),
  );
};

export const isRuleDisabled = (
  highlightText: string,
  value: string | Array<string>,
): boolean => {
  if (highlightText) {
    const lowerHighlight = highlightText.toLowerCase();
    if (Array.isArray(value)) {
      return !value.some((val) => val.toLowerCase().includes(lowerHighlight));
    }
    return !value.toLowerCase().includes(lowerHighlight);
  }
  return false;
};

export const normalizeValueToArray = (
  value: string | Array<string> | null | undefined,
) => {
  if (value === null || value === undefined || value === INCLUDE_ALL_VALUE) {
    return [];
  } else if (typeof value === 'string') {
    return [value];
  } else if (Array.isArray(value)) {
    return value;
  }
  return [];
};
